import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';

import type { OrderType, TimeInForce } from '@weke/domain';
import type { OrderPreview, PreviewRequest, PreviewSymbol, ResponsePayload } from '@weke/protocol';

import { terminal } from '../app/terminal.js';
import { useApp, useQuotes } from '@weke/ui';
import { hasFeature } from '@weke/ui';
import {
  formatMoney,
  formatPercentBp,
  formatPrice,
  formatVolume,
  priceDigits,
  parsePriceInput,
  parseVolumeInput,
  snapToTick,
  stepVolume,
} from '@weke/ui';
import { explainRejection } from '@weke/ui';
import type { Explanation } from '@weke/ui';
import { orderTypeNeeds, tifsFor, typesForSymbol } from '../lib/order-form.js';
import { Panel } from '@weke/ui';

type Side = 'BUY' | 'SELL';

interface FormState {
  readonly side: Side;
  readonly type: OrderType;
  readonly tif: TimeInForce;
  readonly volume: string;
  readonly limit: string;
  readonly stop: string;
  readonly sl: string;
  readonly tp: string;
}

function initialForm(): FormState {
  return {
    side: 'BUY',
    type: 'MARKET',
    tif: 'IOC',
    volume: '0.10',
    limit: '',
    stop: '',
    sl: '',
    tp: '',
  };
}

/**
 * The order ticket.
 *
 * Three rules it follows, in order of importance:
 *
 * 1. **The numbers come from the server.** Required margin, commission and free
 *    margin after are rendered from `POST /preview`, which runs the real command
 *    through the real engine on a copy of state (`services/trade/src/preview.ts`).
 *    The ticket computes no margin, no commission and no P&L. Its own arithmetic
 *    is limited to parsing what the trader typed into the scaled integer the wire
 *    wants, and checking the volume against the min/max/step the server sent.
 * 2. **A rejection is words, and the ticket survives it.** The engine's code is
 *    mapped by `lib/messages.ts` into a headline and a sentence; the fields stay
 *    exactly as they were, so a refused order can be corrected rather than
 *    retyped. The code stays visible on the message for support.
 * 3. **Keyboard-first.** Alt+B / Alt+S choose the side, Enter sends, Escape
 *    clears a message, and every control is a real focusable element in order.
 *    A trader can place an order without touching the mouse.
 */
export function OrderTicketPanel(): ReactElement {
  const app = useApp();
  const { quotes } = useQuotes();
  const [form, setForm] = useState<FormState>(initialForm);
  const [preview, setPreview] = useState<OrderPreview | null>(null);
  const [previewAt, setPreviewAt] = useState<string | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formNotice, setFormNotice] = useState<Explanation | null>(null);
  const [lastAccepted, setLastAccepted] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const symbol = useMemo<PreviewSymbol | undefined>(
    () => app.symbols.find((entry) => entry.name === app.selectedSymbol) ?? app.symbols[0],
    [app.selectedSymbol, app.symbols],
  );
  const previewSeq = useRef(0);

  /**
   * The licence's order-type restriction, if any.
   *
   * The server refuses a pending order the licence does not include with
   * `E_ENTITLEMENT`; this only stops the ticket offering a choice that would be
   * refused. Market entry and every close stay available in every licence state.
   */
  const pendingOrders = hasFeature(app.session?.features, 'core.pending_orders');
  const typeOptions = useMemo(
    () =>
      symbol === undefined
        ? []
        : typesForSymbol(symbol.allowed_order_types, { pendingOrders }),
    [pendingOrders, symbol],
  );
  useEffect(() => {
    if (typeOptions.length === 0) return;
    if (typeOptions.some((entry) => entry.id === form.type)) return;
    setForm((current) => ({ ...current, type: typeOptions[0]?.id ?? 'MARKET', tif: 'IOC' }));
  }, [form.type, typeOptions]);

  const quote = symbol === undefined ? undefined : quotes[symbol.name];
  const digits = symbol === undefined ? 5 : priceDigits(symbol.digits, symbol.tick_size_e8);

  /**
   * A grid-aligned price for a pending order field: the side's touch price,
   * snapped to the instrument's tick grid in the direction that keeps the order
   * non-marketable (a buy limit below the ask, a buy stop above it). Empty when
   * there is no quote — prefilling `0.00000` would look like a price.
   */
  const touchPrice = (side: Side, kind: 'limit' | 'stop'): string => {
    if (quote === undefined || symbol === undefined) return '';
    const value = side === 'BUY' ? quote.ask : quote.bid;
    const direction: 'down' | 'up' = kind === 'limit'
      ? side === 'BUY'
        ? 'down'
        : 'up'
      : side === 'BUY'
        ? 'up'
        : 'down';
    return formatPrice(snapToTick(value, symbol.tick_size_e8, direction), symbol.digits);
  };

  /* A new symbol means new bounds, new prices, and no stale preview. */
  useEffect(() => {
    setForm((current) => ({
      ...initialForm(),
      side: current.side,
      // Keep the trader's size if the new symbol accepts it, otherwise start at
      // the symbol's own step above its minimum.
      volume:
        symbol === undefined
          ? current.volume
          : volumeOrDefault(symbol, current.volume),
    }));
    setPreview(null);
    setPreviewAt(null);
    setFormError(null);
    setFormNotice(null);
  }, [symbol]);
  const parsed = useMemo(() => {
    if (symbol === undefined) return { ok: false as const, message: 'No symbol selected.' };
    const volume = parseVolumeInput(form.volume, symbol.volume_step_e4, symbol.volume_min_e4, symbol.volume_max_e4);
    if (!volume.ok) return volume;
    const needs = orderTypeNeeds(form.type);
    let limitPrice: string | undefined;
    let stopPrice: string | undefined;
    if (needs.limit) {
      const limit = parsePriceInput(form.limit, digits, symbol.tick_size_e8, 'Limit price');
      if (!limit.ok) return limit;
      limitPrice = limit.value;
    }
    if (needs.stop) {
      const stop = parsePriceInput(form.stop, digits, symbol.tick_size_e8, 'Stop price');
      if (!stop.ok) return stop;
      stopPrice = stop.value;
    }
    let sl: string | undefined;
    let tp: string | undefined;
    if (form.sl.trim().length > 0) {
      const parsedSl = parsePriceInput(form.sl, digits, symbol.tick_size_e8, 'Stop loss');
      if (!parsedSl.ok) return parsedSl;
      sl = parsedSl.value;
    }
    if (form.tp.trim().length > 0) {
      const parsedTp = parsePriceInput(form.tp, digits, symbol.tick_size_e8, 'Take profit');
      if (!parsedTp.ok) return parsedTp;
      tp = parsedTp.value;
    }
    return {
      ok: true as const,
      place: {
        symbol_id: symbol.id,
        side: form.side,
        type: form.type,
        tif: form.tif,
        volume: volume.value,
        ...(limitPrice === undefined ? {} : { limit_price: limitPrice }),
        ...(stopPrice === undefined ? {} : { stop_price: stopPrice }),
        ...(sl === undefined ? {} : { sl }),
        ...(tp === undefined ? {} : { tp }),
      } satisfies NonNullable<PreviewRequest['place']>,
    };
  }, [digits, form, symbol]);

  /* Ask the server for the preview whenever the order (or symbol) changes. */
  useEffect(() => {
    if (!parsed.ok) {
      setPreview(null);
      setFormError(parsed.message);
      return;
    }
    setFormError(null);
    const seq = previewSeq.current + 1;
    previewSeq.current = seq;
    setPreviewBusy(true);
    const timer = setTimeout(() => {
      void terminal.preview(parsed.place).then((response) => {
        // A slower reply must not overwrite a newer preview.
        if (previewSeq.current !== seq) return;
        setPreviewBusy(false);
        setPreview(response?.preview ?? null);
        setPreviewAt(response?.preview?.at ?? null);
      });
    }, 220);
    return () => clearTimeout(timer);
  }, [parsed]);

  const submit = useCallback(async () => {
    if (!parsed.ok) {
      setFormError(parsed.message);
      return;
    }
    setSubmitting(true);
    setFormNotice(null);
    try {
      const response: ResponsePayload = await terminal.place(parsed.place);
      if (response.kind === 'ACCEPTED') {
        setLastAccepted(
          `Order ${response.orderId.slice(0, 8)} accepted${response.idempotentReplay ? ' (replay of an existing request)' : ''}.`,
        );
        setFormNotice(null);
        terminal.reportResponse(response, 'Order accepted');
        void terminal.pollSnapshot();
        void terminal.refreshDeals();
      } else if (response.kind === 'REJECTED') {
        // The message and the fields both stay: the trader corrects, not retypes.
        setFormNotice(explainRejection(response.code));
        terminal.reportResponse(response, '');
      } else if (response.kind === 'REQUOTE') {
        setFormNotice(explainRejection('E_PRICE_SLIPPAGE'));
        terminal.reportResponse(response, '');
      }
    } catch (error) {
      const message =
        (error as { message?: string }).message ?? 'The command could not be sent.';
      setFormError(message);
    } finally {
      setSubmitting(false);
      setPreviewBusy(false);
    }
  }, [parsed]);

  /* Alt+B / Alt+S choose the side from anywhere in the app. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.altKey && !event.ctrlKey && !event.metaKey) {
        if (event.key.toLowerCase() === 'b') {
          event.preventDefault();
          setForm((current) => ({ ...current, side: 'BUY' }));
        } else if (event.key.toLowerCase() === 's') {
          event.preventDefault();
          setForm((current) => ({ ...current, side: 'SELL' }));
        }
      }
      if (event.key === 'Escape' && formNotice !== null) {
        setFormNotice(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [formNotice]);

  if (symbol === undefined) {
    return (
      <Panel id="ticket" title="Order ticket">
        <p className="muted p-3 text-xs">Waiting for the symbol catalogue…</p>
      </Panel>
    );
  }

  const needs = orderTypeNeeds(form.type);
  const account = app.account;

  return (
    <Panel
      id="ticket"
      title="Order ticket"
      badge={symbol.name}
      bodyClassName="panel-body p-2"
    >
      <form
        className="flex h-full flex-col gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        aria-label="Order ticket"
      >
        <div className="grid grid-cols-2 gap-1" role="group" aria-label="Side">
          {(['BUY', 'SELL'] as const).map((side) => (
            <button
              key={side}
              type="button"
              className={`btn ${form.side === side ? (side === 'BUY' ? 'btn-buy' : 'btn-sell') : ''}`}
              aria-pressed={form.side === side}
              onClick={() => setForm((current) => ({ ...current, side }))}
              data-testid={`ticket-side-${side.toLowerCase()}`}
            >
              {side === 'BUY' ? 'Buy / long' : 'Sell / short'}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-0.5">
            <span className="label">Type</span>
            <select
              className="field text-left"
              value={form.type}
              onChange={(event) =>
                setForm((current) => {
                  const type = event.target.value as OrderType;
                  const allowed = tifsFor(type);
                  const tif: TimeInForce = allowed.some((entry) => entry.id === current.tif)
                    ? current.tif
                    : ((allowed[0]?.id as TimeInForce | undefined) ?? 'IOC');
                  return {
                    ...current,
                    type,
                    tif,
                    // Prefill a pending price with the side's touch price, so the
                    // field starts somewhere real and the tick grid is visible.
                    limit:
                      orderTypeNeeds(type).limit && current.limit.length === 0
                        ? touchPrice(current.side, 'limit')
                        : current.limit,
                    stop:
                      orderTypeNeeds(type).stop && current.stop.length === 0
                        ? touchPrice(current.side, 'stop')
                        : current.stop,
                  };
                })
              }
              aria-label="Order type"
              data-testid="ticket-type"
            >
              {typeOptions.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="label">Time in force</span>
            <select
              className="field text-left"
              value={form.tif}
              onChange={(event) =>
                setForm((current) => ({ ...current, tif: event.target.value as TimeInForce }))
              }
              aria-label="Time in force"
              data-testid="ticket-tif"
            >
              {tifsFor(form.type).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex flex-col gap-0.5">
          <span className="label">Volume (lots)</span>
          <div className="flex items-stretch gap-1">
            <button
              type="button"
              className="btn w-6 px-0"
              aria-label="Decrease volume"
              onClick={() =>
                setForm((current) => ({
                  ...current,
                  volume: formatVolume(
                    stepVolume(
                      parsed.ok ? parsed.place.volume : symbol.volume_min_e4,
                      -1,
                      symbol.volume_step_e4,
                      symbol.volume_min_e4,
                      symbol.volume_max_e4,
                    ),
                    symbol.volume_step_e4,
                  ),
                }))
              }
            >
              −
            </button>
            <input
              className={`field flex-1 text-center ${parsed.ok ? '' : 'field-invalid'}`}
              value={form.volume}
              onChange={(event) => setForm((current) => ({ ...current, volume: event.target.value }))}
              inputMode="decimal"
              aria-label="Volume in lots"
              data-testid="ticket-volume"
            />
            <button
              type="button"
              className="btn w-6 px-0"
              aria-label="Increase volume"
              onClick={() =>
                setForm((current) => ({
                  ...current,
                  volume: formatVolume(
                    stepVolume(
                      parsed.ok ? parsed.place.volume : symbol.volume_min_e4,
                      1,
                      symbol.volume_step_e4,
                      symbol.volume_min_e4,
                      symbol.volume_max_e4,
                    ),
                    symbol.volume_step_e4,
                  ),
                }))
              }
            >
              +
            </button>
            <span className="faint self-center text-2xs">
              {formatVolume(symbol.volume_min_e4, symbol.volume_step_e4)}–
              {formatVolume(symbol.volume_max_e4, symbol.volume_step_e4)}
            </span>
          </div>
        </div>

        {needs.stop ? (
          <label className="flex items-center gap-2">
            <span className="label w-20">Stop price</span>
            <input
              className="field flex-1"
              value={form.stop}
              onChange={(event) => setForm((current) => ({ ...current, stop: event.target.value }))}
              inputMode="decimal"
              aria-label="Stop price"
              data-testid="ticket-stop-price"
            />
          </label>
        ) : null}
        {needs.limit ? (
          <label className="flex items-center gap-2">
            <span className="label w-20">Limit price</span>
            <input
              className="field flex-1"
              value={form.limit}
              onChange={(event) => setForm((current) => ({ ...current, limit: event.target.value }))}
              inputMode="decimal"
              aria-label="Limit price"
              data-testid="ticket-limit-price"
            />
          </label>
        ) : null}

        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-0.5">
            <span className="label">Stop loss</span>
            <input
              className="field"
              value={form.sl}
              placeholder="none"
              onChange={(event) => setForm((current) => ({ ...current, sl: event.target.value }))}
              inputMode="decimal"
              aria-label="Stop loss"
              data-testid="ticket-sl"
            />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="label">Take profit</span>
            <input
              className="field"
              value={form.tp}
              placeholder="none"
              onChange={(event) => setForm((current) => ({ ...current, tp: event.target.value }))}
              inputMode="decimal"
              aria-label="Take profit"
              data-testid="ticket-tp"
            />
          </label>
        </div>

        <div className="rounded border border-line bg-bg/60 p-2" data-testid="margin-preview">
          <div className="mb-1 flex items-center justify-between">
            <span className="label">Margin preview</span>
            <span className="faint text-2xs">{previewBusy ? 'asking the server…' : previewAt === null ? 'server numbers' : 'from the engine'}</span>
          </div>
          {!parsed.ok ? (
            <p className="text-2xs text-sell">{parsed.message}</p>
          ) : preview === null ? (
            <p className="muted text-2xs">Waiting for the server…</p>
          ) : (
            <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 text-2xs">
              <dt className="muted">Required margin</dt>
              <dd className="num text-right" data-testid="preview-margin">
                {formatMoney(preview.required_margin_minor, account?.digits ?? 2)}
              </dd>
              <dt className="muted">Commission</dt>
              <dd className="num text-right" data-testid="preview-commission">
                {formatMoney(preview.commission_minor, account?.digits ?? 2)}
              </dd>
              <dt className="muted">Free margin after</dt>
              <dd
                className={`num text-right ${BigInt(preview.free_margin_after_minor) < 0n ? 'loss' : ''}`}
                data-testid="preview-free-margin"
              >
                {formatMoney(preview.free_margin_after_minor, account?.digits ?? 2)}
              </dd>
              <dt className="muted">Margin level after</dt>
              <dd className="num text-right" data-testid="preview-margin-level">
                {preview.margin_level_after_bp === null
                  ? '∞ (nothing reserved)'
                  : formatPercentBp(preview.margin_level_after_bp)}
              </dd>
            </dl>
          )}
          {preview !== null && !preview.margin_reserved && preview.accepted ? (
            <p className="faint mt-1 text-2xs">
              A resting order reserves no margin until it triggers.
            </p>
          ) : null}
          {preview !== null && !preview.accepted && preview.rejection_code !== null ? (
            <p className="mt-1 text-2xs text-sell" data-testid="preview-rejection">
              Would be refused: {explainRejection(preview.rejection_code).title}.{' '}
              {explainRejection(preview.rejection_code).detail}
            </p>
          ) : null}
        </div>

        {formError !== null ? (
          <p className="text-2xs text-sell" role="alert" data-testid="ticket-error">
            {formError}
          </p>
        ) : null}
        {formNotice !== null ? (
          <div className="border border-[#7a2a26] bg-[#2a1210] p-2" role="alert" data-testid="ticket-rejection">
            <p className="text-2xs font-semibold text-sell">{formNotice.title}</p>
            <p className="text-2xs text-muted">{formNotice.detail}</p>
            <p className="faint mt-0.5 text-2xs">The order was not placed. Nothing changed.</p>
          </div>
        ) : null}
        {lastAccepted !== null && formNotice === null ? (
          <p className="text-2xs gain" data-testid="ticket-accepted">
            {lastAccepted}
          </p>
        ) : null}

        {/*
         * The trading controls sit at the bottom, in the order a dealer reads
         * them: the price they can trade at, then the two directions. Clicking a
         * price chooses that side, so the price and the button always agree.
         */}
        <div className="mt-auto flex items-stretch gap-1" aria-label="Market prices">
          <button
            type="button"
            className={`num flex-1 border px-1 py-1 text-right text-sm ${
              form.side === 'SELL'
                ? 'border-[#7a2a26] bg-[#451715] text-sell'
                : 'border-line-strong bg-[#141414] text-text hover:border-[#7a2a26]'
            }`}
            onClick={() => setForm((current) => ({ ...current, side: 'SELL' }))}
            title="Sell at the bid"
            data-testid="ticket-bid"
          >
            {quote === undefined ? '—' : formatPrice(quote.bid, digits)}
            <span className="ml-1 text-2xs faint">bid</span>
          </button>
          <button
            type="button"
            className={`num flex-1 border px-1 py-1 text-left text-sm ${
              form.side === 'BUY'
                ? 'border-[#1f6b3f] bg-[#123d28] text-buy'
                : 'border-line-strong bg-[#141414] text-text hover:border-[#1f6b3f]'
            }`}
            onClick={() => setForm((current) => ({ ...current, side: 'BUY' }))}
            title="Buy at the ask"
            data-testid="ticket-ask"
          >
            {quote === undefined ? '—' : formatPrice(quote.ask, digits)}
            <span className="ml-1 text-2xs faint">ask</span>
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="submit"
            className={`btn flex-1 ${form.side === 'BUY' ? 'btn-buy' : 'btn-sell'}`}
            disabled={submitting || !parsed.ok || !app.accepting}
            data-testid="ticket-submit"
          >
            {submitting
              ? 'Sending…'
              : `${form.side === 'BUY' ? 'Buy' : 'Sell'} ${form.volume} ${symbol.name}`}
          </button>
        </div>
        <p className="faint text-2xs">
          Alt+B / Alt+S choose the side · Enter sends · Esc dismisses a message
        </p>
      </form>
    </Panel>
  );
}

/** A volume the new symbol accepts, or the smallest valid one above its minimum. */
function volumeOrDefault(symbol: PreviewSymbol, previous: string): string {
  const parsed = parseVolumeInput(
    previous,
    symbol.volume_step_e4,
    symbol.volume_min_e4,
    symbol.volume_max_e4,
  );
  if (parsed.ok) return formatVolume(parsed.value, symbol.volume_step_e4);
  return formatVolume(symbol.volume_min_e4, symbol.volume_step_e4);
}




